/**
 * Whether the visitor is on a phone — and so is stopped at `PhoneOnlyScreen` instead of entering
 * the app. The stop comes before sign-in: a phone that could sign up and sign in only to be turned away
 * afterwards was told to use the mobile app one account too late.
 *
 * The web app is the trainer's desk: a plan grid, a calendar, tables sized against a 1280px window.
 * On a phone the MoveBuddy mobile app is the one to use, and the owner's call (24 September 2026) is
 * that a phone gets only that message — no way past it.
 *
 * ── What counts as a phone ───────────────────────────────────────────────────────────────────
 * A touch-only device (no hover, coarse pointer) whose physical screen has a SHORTER side of at most
 * 600 CSS pixels. The screen and not the page, because a browser's "desktop site" mode widens the page
 * to about 980px while the screen stays the phone's; the shorter side, because a phone turned to
 * landscape is still a phone; touch-only, because a narrow desktop window is a trainer who resized it.
 * Tablets have both sides above 600 and are let in.
 */

export const TOUCH_ONLY_QUERY = '(hover: none) and (pointer: coarse)';
export const PHONE_MAX_SHORT_SIDE = 600;

type MatchMedia = (query: string) => { matches: boolean };
type ScreenSize = { width: number; height: number };

/** `true` on a phone. Without `matchMedia` or a screen (some embedded contexts) the answer is no. */
export function isPhone(matchMedia: MatchMedia | undefined, screen: ScreenSize | undefined): boolean {
  if (matchMedia === undefined || screen === undefined) return false;
  try {
    return (
      matchMedia(TOUCH_ONLY_QUERY).matches && Math.min(screen.width, screen.height) <= PHONE_MAX_SHORT_SIDE
    );
  } catch {
    return false;
  }
}
